import { FlashToaster } from "~/components/flash-toaster";
import { Toaster } from "~/components/ui/sonner";
import InternalMessageToastListener from "~/components/chat/internal-message-toast-listener";
import { MiniTaskPopup } from "~/components/mini-tasks/mini-task-popup";
import { PushNotificationPrompt } from "~/components/push-notification-prompt";
import { TicketAssignedAlert } from "~/components/tickets/ticket-assigned-alert";
import { GameAlertListener } from "~/components/channels/welcome/games/game-alert-listener";
import GlobalVideoCallListener from "~/components/video-calls/global-video-call-listener";
import { MiniTasksProvider } from "~/hooks/use-mini-tasks";
import i18n from "~/i18n";
import AppLayoutTemplate from "~/layouts/app/app-header-layout";
import { loadLocale } from "~/lib/zod-utils";
import { type BreadcrumbItem, type SharedData } from "~/types";
import { setLayoutProps, usePage } from "@inertiajs/react";
import { createContext, useContext, useEffect, type ReactNode } from "react";

const PersistentAppLayoutContext = createContext(false);

export interface AppLayoutProps {
  children?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  hideHeaderOnMobile?: boolean;
  bottomBar?: ReactNode;
}

export default function AppLayout({ children, bottomBar, ...props }: AppLayoutProps) {
  const page = usePage<SharedData>();
  const { auth } = page.props;
  const isNestedInPersistentLayout = useContext(PersistentAppLayoutContext);

  useEffect(() => {
    if (!auth.user?.locale) {
      return;
    }
    i18n.changeLanguage(auth.user.locale);
    loadLocale(auth.user.locale);
  }, [auth.user]);

  if (isNestedInPersistentLayout) {
    setLayoutProps<AppLayoutProps>({
      breadcrumbs: props.breadcrumbs,
      hideHeaderOnMobile: props.hideHeaderOnMobile,
      bottomBar,
    });

    return <>{children}</>;
  }

  return (
    <>
      <FlashToaster />
      <Toaster position="top-right" richColors />
      <GlobalVideoCallListener />
      <InternalMessageToastListener />
      <PushNotificationPrompt />
      <TicketAssignedAlert />
      <GameAlertListener />
      <MiniTasksProvider>
        <MiniTaskPopup />
        <AppLayoutTemplate
          breadcrumbs={props.breadcrumbs}
          hideHeaderOnMobile={props.hideHeaderOnMobile}
          bottomBar={bottomBar}
        >
          <PersistentAppLayoutContext.Provider value>
            {children}
          </PersistentAppLayoutContext.Provider>
        </AppLayoutTemplate>
      </MiniTasksProvider>
    </>
  );
}
